import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { X, Pin, PinOff, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Check, XCircle } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { Badge } from "@/ui/components/ui/badge";
import { Alert, AlertStatus } from "@/alerts/types/machine-alert";
import { TruckEvent } from "@/machines/types/event";
import { formatDate, formatNumber } from "@/shared/lib/i18n/formatters.ts";
import { alertStatusLabel } from "@/machines/utils/alert-status-label.ts";
import { cn } from "@/ui/lib/utils";
import { useTimelineContext } from "@/machines/context/timeline-context";
import { useSelectedTime } from "@/machines/context/cursor-store.ts";
import ImagePreview from "../timeline-editor/image-preview/image-preview";
import { useUpdateAlert, useGetAlertById } from "@/alerts/api/queries/machine-alerts.queries";
import { toast } from "react-hot-toast";

const formatSeconds = (value: number | undefined, t: TFunction<"machines">) =>
  value == null
    ? undefined
    : t("units.secondsCompact", {
        value: formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      });

interface MapEventOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  alert?: Alert | null;
  truckEvent?: TruckEvent | null;
  onNavigateToTimeline?: (timestamp: number) => void;
  allAlerts?: Alert[];
  allTruckEvents?: TruckEvent[];
  onAlertChange?: (alert: Alert) => void;
  onTruckEventChange?: (truckEvent: TruckEvent) => void;
}

const MapEventOverlay = ({
  isOpen,
  onClose,
  alert,
  truckEvent,
  onNavigateToTimeline,
  allAlerts = [],
  allTruckEvents = [],
  onAlertChange,
  onTruckEventChange,
}: MapEventOverlayProps) => {
  const { t } = useTranslation("machines");
  const [isPinned, setIsPinned] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Get currently selected event and time from timeline context
  const { currentEvents, selectedTrack } = useTimelineContext();
  const selectedTime = useSelectedTime();

  // Mutation for updating alert status
  const updateAlertMutation = useUpdateAlert();

  // Fetch alert details with images
  const { data: alertDetailData, isLoading: isLoadingAlertDetail } = useGetAlertById(alert?.id, !!alert?.id);

  // Sort alerts and truck events by timestamp in ascending order (oldest first)
  // This ensures prev goes to older dates and next goes to newer dates
  const sortedAlerts = useMemo(() => {
    return [...allAlerts].sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeA - timeB; // Ascending order (oldest first)
    });
  }, [allAlerts]);

  const sortedTruckEvents = useMemo(() => {
    return [...allTruckEvents].sort((a, b) => {
      const timeA = new Date(a.startTime).getTime();
      const timeB = new Date(b.startTime).getTime();
      return timeA - timeB; // Ascending order (oldest first)
    });
  }, [allTruckEvents]);

  const primaryEvent = useMemo(() => {
    if (alert || truckEvent) return null;
    if (!currentEvents) return null;

    // Priority order: dump, dump-no-truck, then any other event
    const dump = currentEvents.layers['dump'];
    const dumpNoTruck = currentEvents.layers['dump-no-truck'];
    const firstLayer = Object.values(currentEvents.layers)[0];

    return dump || dumpNoTruck || firstLayer || currentEvents.truck_event;
  }, [currentEvents, alert, truckEvent]);

  // Determine what data to display based on selectedTrack
  const displayData = useMemo(() => {
    const isTruckTrackSelected = selectedTrack === 'truck_cycles' || selectedTrack === 'truck-cycle';
    const isAlertTrackSelected = selectedTrack === 'alerts';

    // Get truck event from current events layers
    const currentTruckEvent = currentEvents?.layers['truck'] || currentEvents?.truck_event;

    console.log('Overlay displayData calculation:', {
      selectedTrack,
      isTruckTrackSelected,
      isAlertTrackSelected,
      truckEvent: truckEvent?.eventId,
      currentTruckEvent: currentTruckEvent?.startTime,
      alert: alert?.id,
      primaryEvent: primaryEvent?.startTime
    });

    // If truck track is selected, prioritize truck event from current events
    if (isTruckTrackSelected) {
      return truckEvent || currentTruckEvent || alert || primaryEvent;
    }

    // If alert track is selected, prioritize alert
    if (isAlertTrackSelected) {
      return alert || truckEvent || primaryEvent;
    }

    // Default priority: alert → truckEvent → primaryEvent
    return alert || truckEvent || primaryEvent;
  }, [alert, truckEvent, primaryEvent, selectedTrack, currentEvents]);

  const hasEventData = currentEvents && Object.keys(currentEvents.layers).length > 0;

  // Auto-hide overlay if not pinned and no data
  useEffect(() => {
    if (!isPinned && !displayData && !hasEventData && isOpen) {
      onClose();
    }
  }, [isPinned, displayData, hasEventData, isOpen, onClose]);

  const handleClose = () => {
    if (!isPinned) {
      onClose();
    }
  };

  const handlePinToggle = () => {
    setIsPinned(!isPinned);
  };

  const handleCollapseToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleResolve = async () => {
    if (!alert?.id) {
      toast.error(t("alertDetail.toast.idNotFound"));
      return;
    }

    setIsUpdating(true);
    try {
      await updateAlertMutation.mutateAsync({
        id: alert.id.toString(),
        data: { status: AlertStatus.Resolved }
      });

      toast.success(t("mapOverlay.toast.markedResolved"));
    } catch (error) {
      toast.error(t("alertDetail.toast.updateFailed"));
      console.error("Error updating alert:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleMarkAsFalse = async () => {
    if (!alert?.id) {
      toast.error(t("alertDetail.toast.idNotFound"));
      return;
    }

    setIsUpdating(true);
    try {
      await updateAlertMutation.mutateAsync({
        id: alert.id.toString(),
        data: { status: AlertStatus.Ignored }
      });

      toast.success(t("mapOverlay.toast.markedFalse"));
    } catch (error) {
      toast.error(t("alertDetail.toast.updateFailed"));
      console.error("Error updating alert:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePrev = () => {
    if (!alert || sortedAlerts.length === 0) return;

    const currentIndex = sortedAlerts.findIndex(a => a.id === alert.id);
    if (currentIndex === -1 || currentIndex <= 0) return; // Already at first alert

    const prevAlert = sortedAlerts[currentIndex - 1]; // Go to previous (older) alert

    // Navigate to the previous alert's timestamp
    if (prevAlert.gps?.timestamp) {
      const timestamp = new Date(prevAlert.gps.timestamp).getTime();
      onNavigateToTimeline?.(timestamp);
    }

    // Update the alert in the parent component
    onAlertChange?.(prevAlert);
  };

  const handleNext = () => {
    if (!alert || sortedAlerts.length === 0) return;

    const currentIndex = sortedAlerts.findIndex(a => a.id === alert.id);
    if (currentIndex >= sortedAlerts.length - 1) return; // Already at last alert

    const nextAlert = sortedAlerts[currentIndex + 1]; // Go to next (newer) alert

    // Navigate to the next alert's timestamp
    if (nextAlert.gps?.timestamp) {
      const timestamp = new Date(nextAlert.gps.timestamp).getTime();
      onNavigateToTimeline?.(timestamp);
    }

    // Update the alert in the parent component
    onAlertChange?.(nextAlert);
  };

  const handleTruckEventPrev = () => {
    if (!truckEvent || sortedTruckEvents.length === 0) return;

    const currentIndex = sortedTruckEvents.findIndex(te => te.eventId === truckEvent.eventId);
    if (currentIndex === -1 || currentIndex <= 0) return; // Already at first truck event

    const prevTruckEvent = sortedTruckEvents[currentIndex - 1];

    // Navigate to the previous truck event's timestamp
    if (prevTruckEvent.startTime) {
      const timestamp = new Date(prevTruckEvent.startTime).getTime();
      onNavigateToTimeline?.(timestamp);
    }

    // Update the truck event in the parent component
    onTruckEventChange?.(prevTruckEvent);
  };

  const handleTruckEventNext = () => {
    if (!truckEvent || sortedTruckEvents.length === 0) return;

    const currentIndex = sortedTruckEvents.findIndex(te => te.eventId === truckEvent.eventId);
    if (currentIndex >= sortedTruckEvents.length - 1) return; // Already at last truck event

    const nextTruckEvent = sortedTruckEvents[currentIndex + 1];

    // Navigate to the next truck event's timestamp
    if (nextTruckEvent.startTime) {
      const timestamp = new Date(nextTruckEvent.startTime).getTime();
      onNavigateToTimeline?.(timestamp);
    }

    // Update the truck event in the parent component
    onTruckEventChange?.(nextTruckEvent);
  };

  const getStatusColor = (status: AlertStatus) => {
    switch (status) {
      case AlertStatus.Pending:
        return "bg-yellow-500/10 text-yellow-600 border-yellow-500/20";
      case AlertStatus.Resolved:
        return "bg-green-500/10 text-green-600 border-green-500/20";
      case AlertStatus.Ignored:
        return "bg-gray-500/10 text-gray-600 border-gray-500/20";
      default:
        return "bg-gray-500/10 text-gray-600 border-gray-500/20";
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        "absolute top-4 right-4 z-50 w-80 bg-white dark:bg-sidebar shadow-2xl rounded-lg transform transition-all duration-300 flex flex-col max-h-[calc(100%-2rem)] border border-border",
        isOpen ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-border bg-sidebar/50 rounded-t-lg">
        <h3 className="text-sm font-semibold">
          {alert ? t("eventDetails.alertTitle") : truckEvent ? t("mapOverlay.truckEventTitle") : t("eventDetails.eventTitle")}
        </h3>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCollapseToggle}
            className="h-7 w-7"
            title={isCollapsed ? t("mapOverlay.expand") : t("mapOverlay.collapse")}
          >
            {isCollapsed ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronUp className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePinToggle}
            className="h-7 w-7"
            title={isPinned ? t("eventDetails.unpin") : t("eventDetails.pin")}
          >
            {isPinned ? (
              <PinOff className="h-3.5 w-3.5" />
            ) : (
              <Pin className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-7 w-7"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="flex-1 overflow-y-auto">
          {!displayData && !hasEventData && selectedTime ? (
            <div className="p-4 flex items-center justify-center">
              <p className="text-xs text-muted-foreground text-center">
                {t("mapOverlay.noEvents")}
              </p>
            </div>
          ) : (
            <div className="p-3 space-y-3">
              {/* Image Preview — for an alert, strictly its own images (with
                  confidence); event frames only when no alert is selected. */}
              <ImagePreview
                images={alertDetailData?.data?.images}
                alertOnly={!!alert}
                isLoading={!!alert && isLoadingAlertDetail}
              />

              {/* Event Info Section */}
              <div className="space-y-2">

            {/* Date Time */}
            <div>
              <p className="text-[10px] text-muted-foreground">{t("mapOverlay.dateTime")}</p>
              <p className="text-xs font-medium">
                {alert
                  ? formatDate(new Date(alert.timestamp), "PP pp")
                  : truckEvent
                  ? formatDate(new Date(truckEvent.startTime), "PP pp")
                  : primaryEvent
                  ? formatDate(new Date(primaryEvent.startTime), "PP pp")
                  : selectedTime
                  ? formatDate(new Date(selectedTime), "PP pp")
                  : t("eventDetails.notAvailable")}
              </p>
            </div>

            {/* Duration (for events and truck events) */}
            {primaryEvent && (
              <div>
                <p className="text-[10px] text-muted-foreground">{t("eventDetails.duration")}</p>
                <p className="text-xs font-medium">{formatSeconds(primaryEvent.durationSeconds, t)}</p>
              </div>
            )}
            {truckEvent && (
              <div>
                <p className="text-[10px] text-muted-foreground">{t("mapOverlay.cycleDuration")}</p>
                <p className="text-xs font-medium">{formatSeconds(truckEvent.cycleDuration, t)}</p>
              </div>
            )}

            {/* All Event Layers */}
            {!alert && currentEvents && Object.keys(currentEvents.layers).length > 0 && (
              <div>
                <p className="text-[10px] text-muted-foreground mb-1">{t("mapOverlay.eventLayers")}</p>
                <div className="flex items-center gap-1 flex-wrap">
                  {Object.entries(currentEvents.layers).map(([key, event]) => (
                    <Badge
                      key={key}
                      variant="outline"
                      style={{ backgroundColor: event?.color + "20", borderColor: event?.color }}
                      className="text-[10px] h-5 px-1.5"
                    >
                      {event?.state || key}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Classification Badges */}
            {!alert && !truckEvent && primaryEvent && (
              <div>
                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                  {primaryEvent?.abnormal && (
                    <Badge variant="destructive" className="text-[10px] h-5 px-1.5">
                      {t("eventDetails.abnormal")}
                    </Badge>
                  )}
                  {primaryEvent?.productive === false && (
                    <Badge variant="secondary" className="text-[10px] h-5 px-1.5">
                      {t("eventDetails.unproductive")}
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {/* Alert Badge */}
            {alert && (
              <div>
                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                  <Badge variant="outline" className="text-[10px] h-5 px-1.5">
                    {alert?.type}
                  </Badge>
                </div>
              </div>
            )}
          </div>

          {/* Truck Event Info Section */}
          {truckEvent && (
            <div className="space-y-2 pt-2 border-t border-border">
              <div className="space-y-1.5">

                {/* Truck Details */}
                <div>
                  <p className="text-[10px] text-muted-foreground">{t("mapOverlay.truckDetails")}</p>
                  <div className="flex flex-col gap-1.5 mt-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{t("mapOverlay.buckets")}</span>
                      <span className="font-medium">{truckEvent?.buckets}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{t("mapOverlay.digTime")}</span>
                      <span className="font-medium">{formatSeconds(truckEvent?.digTime, t)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{t("mapOverlay.swingLoaded")}</span>
                      <span className="font-medium">{formatSeconds(truckEvent?.swingLoadedTime, t)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{t("mapOverlay.fragmentation")}</span>
                      <span className="font-medium">{truckEvent?.fragmentation}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{t("mapOverlay.productivity")}</span>
                      <span className="font-medium">{truckEvent?.productivity == null ? undefined : formatNumber(truckEvent.productivity, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>

                {/* Truck Event Navigation */}
                {sortedTruckEvents.length > 0 && (
                  <div className="space-y-2 mt-3">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-8 text-xs"
                        onClick={handleTruckEventPrev}
                        disabled={sortedTruckEvents.findIndex(te => te.eventId === truckEvent.eventId) <= 0}
                      >
                        <ChevronLeft className="h-3.5 w-3.5 mr-1" />
                        {t("mapOverlay.prev")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-8 text-xs"
                        onClick={handleTruckEventNext}
                        disabled={sortedTruckEvents.findIndex(te => te.eventId === truckEvent.eventId) >= sortedTruckEvents.length - 1}
                      >
                        {t("mapOverlay.next")}
                        <ChevronRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </div>

                    {/* Truck Event counter */}
                    <div className="text-center">
                      <span className="text-[10px] text-muted-foreground">
                        {t("mapOverlay.truckEventCounter", { index: sortedTruckEvents.findIndex(te => te.eventId === truckEvent.eventId) + 1, total: sortedTruckEvents.length })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Alert Info Section */}
          {alert && (
            <div className="space-y-2 pt-2 border-t border-border">

              {/* Alert Details */}
              <div className="space-y-1.5">
                <div className={"pb-4"}>
                  <p className="text-[10px] text-muted-foreground">{t("eventDetails.alertTitle")}</p>
                  <div className={"flex flex-row gap-2 items-center"}>
                    <p className="text-xs font-medium">{alert?.origin}</p>

                    <Badge className={cn("mt-0.5 text-[10px] h-5 px-1.5", getStatusColor(alert?.status))}>
                      {alertStatusLabel(alert.status, t)}
                    </Badge>
                  </div>
                </div>

                {/* Alert Action Buttons - Available for all alert states */}
                <div className="space-y-2 mt-3">
                  {/* First Row: Resolve and Mark as False */}
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      className="flex-1 h-8 text-xs"
                      onClick={handleResolve}
                      disabled={isUpdating}
                    >
                      <Check className="h-3.5 w-3.5 mr-1" />
                      {isUpdating ? t("mapOverlay.updating") : t("mapOverlay.resolve")}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="flex-1 h-8 text-xs"
                      onClick={handleMarkAsFalse}
                      disabled={isUpdating}
                    >
                      <XCircle className="h-3.5 w-3.5 mr-1" />
                      {isUpdating ? t("mapOverlay.updating") : t("mapOverlay.false")}
                    </Button>
                  </div>

                  {/* Second Row: Prev and Next */}
                  {sortedAlerts.length > 0 && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-8 text-xs"
                        onClick={handlePrev}
                        disabled={isUpdating || sortedAlerts.findIndex(a => a.id === alert.id) <= 0}
                      >
                        <ChevronLeft className="h-3.5 w-3.5 mr-1" />
                        {t("mapOverlay.prev")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-8 text-xs"
                        onClick={handleNext}
                        disabled={isUpdating || sortedAlerts.findIndex(a => a.id === alert.id) >= sortedAlerts.length - 1}
                      >
                        {t("mapOverlay.next")}
                        <ChevronRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </div>
                  )}

                  {/* Alert counter */}
                  {sortedAlerts.length > 0 && (
                    <div className="text-center">
                      <span className="text-[10px] text-muted-foreground">
                        {t("mapOverlay.alertCounter", { index: sortedAlerts.findIndex(a => a.id === alert.id) + 1, total: sortedAlerts.length })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MapEventOverlay;
